import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * useElementRefCallback
 *
 * A hook that provides a ref callback and invokes the provided callback:
 * - When the element mounts.
 * - When the element resizes (via ResizeObserver).
 * - When the window resizes.
 *
 * @template T - The type of the HTML element, extending HTMLElement.
 * @param {(element: T | null) => void} [callback] - Optional function executed on mount & resize.
 * @returns {object} An object containing:
 *  - `ref`: A ref object containing the current element reference.
 *  - `refCallback`: A callback function to set the element reference.
 */
export const useElementRefCallback = <T extends HTMLElement = HTMLElement>(callback?: (element: T | null) => void) => {
  const elementRef = useRef<T | null>(null);
  const [isElementMounted, setIsElementMounted] = useState(false);

  const refCallback = useCallback(
    (node: T | null) => {
      if (node) {
        elementRef.current = node;
        setIsElementMounted(true);
        callback?.(elementRef.current);
      }
    },
    [callback],
  );

  useLayoutEffect(() => {
    const element = elementRef.current;

    if (!isElementMounted || !element) {
      return () => {
        /* empty */
      };
    }

    const resizeObserver = new ResizeObserver(() => callback?.(element));
    resizeObserver.observe(element);

    const handleWindowResize = () => callback?.(element);
    window.addEventListener('resize', handleWindowResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [callback, isElementMounted]);

  return { ref: elementRef, refCallback };
};
